/**
 * Phase 8 · C-40 — Position timeline drawer (UI half).
 *
 * Opens from any row in `/reports/tat` (By position tab) **and** from the
 * "View full timeline" button on `RequisitionDetailPage`. Reads
 * `reportsTatService.getPositionTimeline` (C-40 service, OPUS-14).
 *
 * Spec: docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.4
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.4
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Check,
  Download,
  Edit,
  ExternalLink,
  Loader2,
  Mail,
  Phone,
  Plus,
  Sparkles,
  X,
} from 'lucide-react';
import { supabase } from '@core/supabase/client';
import {
  reportsTatService,
  type TimelineEvent,
} from '@core/supabase/services/reportsTatService';
import { Drawer } from '@shared/components/ui/Drawer';
import {
  computeTatStatus,
  TAT_PROGRESS_COLOR,
  TatChip,
} from '@shared/components/ui/TatChip';

// ── Types ────────────────────────────────────────────────────────────────────

export interface PositionTimelineDrawerProps {
  /** Null means the drawer is closed. */
  positionId: string | null;
  onClose: () => void;
}

/** Minimum position metadata needed for the header. */
interface PositionMeta {
  title: string;
  clientName: string;
  openedAt: string | null;
  targetCloseDate: string | null;
  closedAt: string | null;
  tatDaysActual: number | null;
  tatDaysTarget: number | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const ICON_MAP: Record<TimelineEvent['icon'], React.ElementType> = {
  plus:     Plus,
  check:    Check,
  x:        X,
  mail:     Mail,
  phone:    Phone,
  calendar: Calendar,
  edit:     Edit,
  sparkles: Sparkles,
};

function EventIcon({ kind }: { kind: TimelineEvent['icon'] }) {
  const Icon = ICON_MAP[kind];
  return <Icon className="h-4 w-4 mt-0.5 shrink-0 vscode-text-tertiary" aria-hidden />;
}

// ── CSV export ────────────────────────────────────────────────────────────────

function buildCsv(events: TimelineEvent[]): string {
  const escape = (v: string | null | undefined) => {
    const s = String(v ?? '').replace(/"/g, '""');
    return `"${s}"`;
  };
  const header = ['ts', 'kind', 'actor', 'label', 'detail'].join(',');
  const rows = events.map((ev) =>
    [
      escape(ev.ts),
      escape(ev.kind),
      escape(ev.actorName ?? ''),
      escape(ev.label),
      escape(ev.detail ?? ''),
    ].join(','),
  );
  return [header, ...rows].join('\r\n');
}

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Position meta query (single row from the TAT view) ────────────────────────

interface TatViewSlice {
  title: string | null;
  client_id: string | null;
  opened_at: string | null;
  target_close_date: string | null;
  closed_at: string | null;
  tat_days_actual: number | null;
  tat_days_target: number | null;
}

interface ClientNameRow {
  id: string;
  name: string;
}

async function fetchPositionMeta(positionId: string): Promise<PositionMeta | null> {
  const [tatRes, reqRes] = await Promise.all([
    supabase
      .from('ggj_b2b_requisitions_tat_v')
      .select(
        'title, client_id, opened_at, target_close_date, closed_at, tat_days_actual, tat_days_target',
      )
      .eq('id', positionId)
      .maybeSingle(),
    // Fallback: base table in case view is not populated (pre-migration)
    supabase
      .from('ggj_b2b_requisitions')
      .select('title, client_id, org_id')
      .eq('id', positionId)
      .maybeSingle(),
  ]);

  const view = (tatRes.data ?? null) as TatViewSlice | null;
  const base = (reqRes.data ?? null) as {
    title: string | null;
    client_id: string | null;
    org_id: string | null;
  } | null;

  const title = view?.title ?? base?.title ?? 'Position';
  const clientId = view?.client_id ?? base?.client_id ?? null;
  const orgId = (base as { org_id?: string | null } | null)?.org_id ?? null;

  let clientName = '';
  if (clientId && orgId) {
    const { data: cData } = await supabase
      .from('ggj_b2b_clients')
      .select('id, name')
      .eq('id', clientId)
      .eq('org_id', orgId)
      .maybeSingle();
    clientName = (cData as ClientNameRow | null)?.name ?? '';
  }

  return {
    title,
    clientName,
    openedAt: view?.opened_at ?? null,
    targetCloseDate: view?.target_close_date ?? null,
    closedAt: view?.closed_at ?? null,
    tatDaysActual: view?.tat_days_actual ?? null,
    tatDaysTarget: view?.tat_days_target ?? null,
  };
}

// ── Progress bar ──────────────────────────────────────────────────────────────

function TatProgressBar({ meta }: { meta: PositionMeta }) {
  const status = computeTatStatus(meta.targetCloseDate, meta.closedAt);
  const pct =
    meta.tatDaysTarget && meta.tatDaysTarget > 0 && meta.tatDaysActual != null
      ? Math.min(meta.tatDaysActual / meta.tatDaysTarget, 1) * 100
      : 0;
  const colorClass = TAT_PROGRESS_COLOR[status];

  return (
    <div className="mt-2">
      <div className="h-1.5 w-full rounded-full bg-zinc-200 dark:bg-zinc-700">
        <div
          className={`h-1.5 rounded-full transition-all ${colorClass}`}
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="TAT progress"
        />
      </div>
    </div>
  );
}

// ── Event row ─────────────────────────────────────────────────────────────────

function EventRow({ event }: { event: TimelineEvent }) {
  return (
    <li className="flex gap-3 py-3">
      <EventIcon kind={event.icon} />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] leading-snug vscode-text-primary">
          <span className="font-medium">{event.actorName ?? 'System'}</span>{' '}
          {event.label}
        </p>
        {event.detail && (
          <p className="mt-0.5 text-[11px] vscode-text-tertiary">{event.detail}</p>
        )}
        <p className="mt-0.5 text-[11px] text-zinc-400 dark:text-zinc-500">
          {relativeTime(event.ts)} · {formatDate(event.ts)}
        </p>
      </div>
      {event.viewHref && (
        <Link
          to={event.viewHref}
          className="shrink-0 self-start text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline"
          tabIndex={0}
        >
          View
        </Link>
      )}
    </li>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function PositionTimelineDrawer({
  positionId,
  onClose,
}: PositionTimelineDrawerProps) {
  const open = positionId !== null;

  // ── Position metadata ──
  const [meta, setMeta] = useState<PositionMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(false);

  // ── Timeline event pages ──
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [exportLoading, setExportLoading] = useState(false);

  // reset state whenever positionId changes
  useEffect(() => {
    setMeta(null);
    setEvents([]);
    setCursor(undefined);
    setHasMore(false);
    if (!positionId) return;

    let cancelled = false;

    setMetaLoading(true);
    fetchPositionMeta(positionId)
      .then((m) => { if (!cancelled) setMeta(m); })
      .catch((err) => { console.warn('[PositionTimelineDrawer] meta:', err); })
      .finally(() => { if (!cancelled) setMetaLoading(false); });

    setLoading(true);
    reportsTatService
      .getPositionTimeline(positionId, { limit: 200 })
      .then((page) => {
        if (cancelled) return;
        setEvents(page);
        setHasMore(page.length === 200);
        if (page.length > 0) setCursor(page[page.length - 1]!.ts);
      })
      .catch((err) => { console.warn('[PositionTimelineDrawer] timeline:', err); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [positionId]);

  // ── Infinite scroll ──
  const sentinelRef = useRef<HTMLDivElement>(null);

  const loadMore = useCallback(() => {
    if (!positionId || !cursor || loading || !hasMore) return;
    setLoading(true);
    reportsTatService
      .getPositionTimeline(positionId, { limit: 200, before: cursor })
      .then((page) => {
        setEvents((prev) => {
          // Dedupe by id
          const seen = new Set(prev.map((e) => e.id));
          const fresh = page.filter((e) => !seen.has(e.id));
          return [...prev, ...fresh];
        });
        setHasMore(page.length === 200);
        if (page.length > 0) setCursor(page[page.length - 1]!.ts);
      })
      .catch((err) => { console.warn('[PositionTimelineDrawer] loadMore:', err); })
      .finally(() => setLoading(false));
  }, [positionId, cursor, loading, hasMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry?.isIntersecting) loadMore(); },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  // ── CSV export ──
  const handleExport = useCallback(async () => {
    if (!positionId) return;
    setExportLoading(true);
    try {
      const all = await reportsTatService.getPositionTimeline(positionId, { limit: 2000 });
      downloadCsv(buildCsv(all), `timeline-${positionId}.csv`);
    } catch (err) {
      console.warn('[PositionTimelineDrawer] export:', err);
    } finally {
      setExportLoading(false);
    }
  }, [positionId]);

  // ── Drawer content ──
  const headerTitle = useMemo(() => {
    if (!meta) return 'Position timeline';
    return meta.clientName
      ? `${meta.title} · ${meta.clientName}`
      : meta.title;
  }, [meta]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="right"
      panelMaxWidthPx={520}
      ariaLabel="Position timeline"
      showCloseButton={false}
      panelClassName="!overflow-hidden"
    >
      {/* ── Sticky header ── */}
      <header className="shrink-0 border-b vscode-border px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15px] font-semibold vscode-text-primary">
              {metaLoading ? (
                <span className="inline-block h-4 w-48 animate-pulse rounded vscode-bg-hover" />
              ) : (
                headerTitle
              )}
            </p>
            {meta && (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <TatChip
                  targetCloseDate={meta.targetCloseDate}
                  closedAt={meta.closedAt}
                  compact
                />
                {meta.openedAt && (
                  <span className="text-[11px] vscode-text-tertiary">
                    Opened {relativeTime(meta.openedAt)}
                  </span>
                )}
              </div>
            )}
            {meta && (
              <TatProgressBar meta={meta} />
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 vscode-text-tertiary hover:vscode-bg-hover hover:vscode-text-primary transition-colors"
            aria-label="Close timeline"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* CTA row */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {positionId && (
            <Link
              to={`/requisitions/${positionId}`}
              className="inline-flex h-7 items-center gap-1.5 rounded-[8px] border border-emerald-500/40 bg-emerald-500/10 px-2.5 text-[12px] font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20 transition-colors"
            >
              <ExternalLink className="h-3 w-3" aria-hidden />
              Open requisition
            </Link>
          )}
          <button
            type="button"
            onClick={() => { void handleExport(); }}
            disabled={exportLoading}
            className="inline-flex h-7 items-center gap-1.5 rounded-[8px] border vscode-border px-2.5 text-[12px] font-medium vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary transition-colors disabled:opacity-50"
          >
            {exportLoading
              ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              : <Download className="h-3 w-3" aria-hidden />}
            Export timeline (CSV)
          </button>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4">
        {events.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Calendar className="mb-3 h-8 w-8 vscode-text-tertiary" aria-hidden />
            <p className="text-[14px] font-medium vscode-text-primary">No events yet</p>
            <p className="mt-1 text-[12px] vscode-text-tertiary">
              Events will appear as this position progresses.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-200 dark:divide-zinc-700" role="list">
            {events.map((ev) => (
              <EventRow key={ev.id} event={ev} />
            ))}
          </ul>
        )}

        {/* Loading skeleton rows */}
        {loading && (
          <ul className="divide-y divide-zinc-200 dark:divide-zinc-700" role="presentation">
            {Array.from({ length: 4 }).map((_, i) => (
              <li key={i} className="flex gap-3 py-3">
                <span className="mt-1 h-4 w-4 shrink-0 animate-pulse rounded vscode-bg-hover" />
                <div className="flex-1 space-y-1.5">
                  <span className="block h-3 w-3/4 animate-pulse rounded vscode-bg-hover" />
                  <span className="block h-2.5 w-1/2 animate-pulse rounded vscode-bg-hover" />
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Infinite-scroll sentinel */}
        {hasMore && !loading && (
          <div ref={sentinelRef} className="py-2 text-center text-[11px] vscode-text-tertiary">
            Scroll for more events…
          </div>
        )}
      </div>
    </Drawer>
  );
}
